import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Paper from "@material-ui/core/Paper";

// Assets
import mainLogo from "../../assets/logos/main-logo.png";

// Config
import { getSettingsUrl, setPinUrl } from "../../config/apiConfig";
import { externalRoutes } from "../../config/routes";
import { snackbarTypes } from "../../config/snackbar";
import { userTypes } from "../../config/values";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useTranslate from "../../hooks/useTranslate";

// Actions
import { parseFetchOptions, onSignOut } from "../../state/user/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Components
import Button from "../../components/Button";
import GoBackLink from "../../components/GoBackLink";
import Link from "../../components/Link";
import PincodeField, { initialPincode } from "../../components/PincodeField";
import Text from "../../components/Text";

const useStyles = makeStyles(theme => ({
  root: {
    width: 640,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column"
  },
  paper: {
    border: styles.values.border
  },
  logo: {
    height: 35,
    marginBottom: "40px"
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "70px 90px 0"
  },
  title: {
    padding: theme.spacing(1.5, 0, 1),
    // color: colors.pictonBlue,
    textTransform: "uppercase"
  },
  description: {
    width: "100%",
    lineHeight: 1,
    marginBottom: theme.spacing(7),
    fontSize: "16px",
    fontWeight: "400"
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: "16px",
    color: colors.riverBed
  },
  pincodeField: {
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1)
  },
  divider: {
    marginBottom: theme.spacing(2),
    width: "100%"
  },
  mismatchError: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2)
  },
  preferencesTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontSize: "16px"
  },
  checkboxLabel: {
    color: colors.santasGray
  },
  goBack: {
    padding: "0",
    marginBottom: theme.spacing(1)
  },
  goBackHighlight: {
    color: colors.pictonBlue
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    padding: "100px 90px"
  },
  footerLink: styles.mixins.link,
  submitButton: {
    flexGrow: 1,
    borderRadius: 12
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    width: "100%"
  }
}));

const PinCreateScreen = ({ onAuthorize }) => {
  const [firstCode, setFirstCode] = useState(initialPincode);
  const [secondCode, setSecondCode] = useState(initialPincode);
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState(null);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const getText = useTranslate();

  useEffect(() => {
    let isMounted = true;
    dispatch(
      parseFetchOptions(options => {
        fetch(getSettingsUrl, options)
          .then(res => res.json())
          .then(data => {
            if (isMounted) setSettings(data);
          })
          .catch(() => trackException("Failed to fetch notification settings"));
      })
    );
    return () => {
      isMounted = false;
    };
  }, [dispatch, trackException]);

  const handleChangeFirst = value => setFirstCode(value);
  const handleChangeSecond = value => setSecondCode(value);
  const handleSignOut = () => dispatch(onSignOut());

  const hasMismatch =
    firstCode.every(digit => digit !== "") &&
    secondCode.every(digit => digit !== "") &&
    firstCode.join("") !== secondCode.join("");

  const updateSetting = type => () => {
    setSettings({
      notifications: {
        ...settings.notifications,
        [type]: !settings.notifications[type]
      }
    });
  };

  const handleSubmit = () => {
    setCreating(true);
    dispatch(
      parseFetchOptions(async ({ headers }) => {
        const response = await fetch(setPinUrl, {
          method: "PUT",
          headers: {
            ...headers,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            pin: firstCode.join("")
          })
        }).catch(() => {});

        if (response.ok) {
          dispatch(
            parseFetchOptions(settingsOptions => {
              fetch(getSettingsUrl, {
                method: "PUT",
                headers: {
                  ...settingsOptions.headers,
                  "Content-Type": "application/json"
                },
                body: JSON.stringify({ notifications: settings.notifications })
              }).catch(() =>
                trackException("Failed to update notification settings")
              );
            })
          );
          onAuthorize();
        } else {
          setFirstCode(initialPincode);
          setSecondCode(initialPincode);
          trackException("Failed to set pin code");
          dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
        }
        setCreating(false);
      })
    );
  };

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.paper}>
        <form className={classes.contentContainer}>
          <img className={classes.logo} src={mainLogo} alt="Kulpa" />
          <GoBackLink
            className={classes.goBack}
            onClick={handleSignOut}
            text="common.valueLabels.backTo"
            values={{
              1: (
                <span key={1} className={classes.goBackHighlight}>
                  {getText("pageTitles.login")}
                </span>
              ),
              isComponent: true
            }}
          />
          <Text
            type="heading1"
            className={classes.title}
            text="createPasscodePage.cardTitle"
          />
          <Text
            className={classes.description}
            text="createPasscodePage.description"
            type="body1"
          />
          <Text
            className={classes.label}
            text="createPasscodePage.title"
            type="contentLabel4"
          />
          <PincodeField
            className={classes.pincodeField}
            disabled={creating}
            onChange={handleChangeFirst}
            value={firstCode}
            autoFocusOwner
          />
          <Text
            className={classes.label}
            text="createPasscodePage.reEnter"
            type="contentLabel4"
          />
          <Divider className={classes.divider} />
          <PincodeField
            className={classes.pincodeField}
            disabled={creating}
            onChange={handleChangeSecond}
            value={secondCode}
          />
          {hasMismatch && (
            <FormHelperText error className={classes.mismatchError}>
              {getText("createPasscodePage.mismatchDialog.description")}
            </FormHelperText>
          )}
          <Text
            type="heading4"
            className={classes.preferencesTitle}
            text="settingsPage.title"
          />
          {settings && (
            <>
              <FormControlLabel
                classes={{ label: classes.checkboxLabel }}
                control={
                  <Checkbox
                    checked={!!settings.notifications.email}
                    onChange={updateSetting("email")}
                    color="primary"
                  />
                }
                label={getText("settingsPage.notifications.allowEmailLabel")}
              />
              <FormControlLabel
                classes={{ label: classes.checkboxLabel }}
                control={
                  <Checkbox
                    checked={!!settings.notifications.push}
                    onChange={updateSetting("push")}
                    color="primary"
                  />
                }
                label={getText("settingsPage.notifications.allowPushLabel")}
              />
            </>
          )}
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleSubmit}
              className={classes.submitButton}
              variant="contained"
              color="primary"
              disableElevation
              disabled={
                creating ||
                firstCode[0] === "" ||
                firstCode.join("") !== secondCode.join("")
              }
            >
              {getText("common.prompts.continue")}
            </Button>
            {creating && <CircularProgress size={32} />}
          </div>
        </form>
        <div className={classes.footer}>
          <Text
            type="subtitleLight"
            align="center"
            text="loginPage.disclaimerLinks"
            values={{
              1: (
                <Link
                  key={1}
                  className={classes.footerLink}
                  target="_blank"
                  to={externalRoutes.terms(userTypes.Victim)}
                >
                  {getText("settingsPage.terms")}
                </Link>
              ),
              2: (
                <Link
                  key={2}
                  className={classes.footerLink}
                  target="_blank"
                  to={externalRoutes.privacy(userTypes.Victim)}
                >
                  {getText("common.valueLabels.privacyNotice")}
                </Link>
              ),
              isComponent: true
            }}
          />
        </div>
      </Paper>
    </div>
  );
};

PinCreateScreen.propTypes = {
  onAuthorize: PropTypes.func.isRequired
};

export default PinCreateScreen;
